import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { object as yupObject, mixed as yupMixed } from 'yup';

import { FormSchemaPropTypes } from './propTypes';

const validatorBuilder = (label, validationRules, yupType, validationContext) =>
  validationRules.reduce((validator, rule) => rule(label, validator, validationContext), yupType);

export const getFilteredInputs = schema => schema.filter(field => !field.options || !field.options.readOnly );

export const getValidationSchema = ({
  filteredInputs,
  validationContext,
}) => {
  const shape = filteredInputs.reduce((result, field) => {
    const label = (field.meta?.label ?? field.name) || 'Field';
    const validationRules = (field.options && field.options.validationRules) || [];
    const yupType = (field.options && field.options.yupType) || yupMixed();
    const builtValidator = validatorBuilder(label, validationRules, yupType, validationContext) || yupType;
    result[field.name] = builtValidator.label(label).typeError(`${label} must be a ${yupType}`); // eslint-disable-line no-template-curly-in-string
    return result;
  }, {});
  return yupObject().shape(shape);
};

const FormikProvider = ({
  schema,
  onSubmit = x => x,
  render,
  entityToEdit,
  validationContext,
}) => {
  const filteredInputs = getFilteredInputs(schema);

  const validationSchema = getValidationSchema({
    filteredInputs,
    validationContext,
  });

  const initialValues = filteredInputs.reduce((result, field) => {
    if (entityToEdit && entityToEdit[field.name] !== null && typeof entityToEdit[field.name] !== 'undefined') {
      result[field.name] = field.additionalProps && field.additionalProps.valueFormatter
        ? field.additionalProps.valueFormatter(entityToEdit[field.name])
        : String(entityToEdit[field.name]);
    } else if (field.meta && typeof field.meta.initialValue !== 'undefined') {
      result[field.name] = field.meta.initialValue;
    } else {
      result[field.name] = '';
    }
    return result;
  }, {});

  const formik = useFormik({
    onSubmit,
    validationSchema,
    initialValues,
    enableReinitialize: true,
  });

  return render({ formik, schema });
};

FormikProvider.propTypes = {
  schema: FormSchemaPropTypes.isRequired,
  onSubmit: PropTypes.func,
  render: PropTypes.func.isRequired,
  entityToEdit: PropTypes.objectOf(PropTypes.any), // eslint-disable-line react/forbid-prop-types
  validationContext: PropTypes.objectOf(PropTypes.any), // eslint-disable-line react/forbid-prop-types
};

export default FormikProvider;
